import apiClient, { ApiResponse, User } from './api';
import { z } from 'zod';

// Validation schemas
const LoginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const RegisterSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain uppercase, lowercase, number and special character'
    ),
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .regex(/^[a-zA-Z\s]+$/, 'Name can only contain letters and spaces'),
  profilePic: z.string().url().optional(),
});

// Types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  profilePic?: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  user: User;
  token: string;
  csrfToken: string;
  redirectUrl: string;
}

export interface LogoutResponse {
  success: boolean;
  message: string;
  redirectUrl: string;
}

/**
 * Authentication service with secure validation and error handling
 */
class AuthService {
  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // Validate input
      const validatedData = LoginSchema.parse(credentials);

      console.log('🔐 Attempting login for:', validatedData.email);

      // Make login request
      const response = await apiClient.post<AuthResponse>('/auth/login', validatedData);

      if (response.data.success && response.data.token) {
        // Store authentication token
        apiClient.setAuthToken(response.data.token);

        console.log('✅ Login successful for:', response.data.user.email);

        return response.data;
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('❌ Login failed:', error);

      // Handle validation errors
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        throw new Error(firstError.message);
      }

      // Handle API errors
      if (error.code) {
        switch (error.code) {
          case 'INVALID_CREDENTIALS':
            throw new Error('Invalid email or password');
          case 'RATE_LIMIT_EXCEEDED':
            throw new Error('Too many login attempts. Please try again later.');
          case 'ACCOUNT_SUSPENDED':
            throw new Error('Your account has been suspended. Please contact support.');
          default:
            throw new Error(error.message || 'Login failed');
        }
      }

      throw new Error(error.message || 'Login failed');
    }
  }

  /**
   * Register new user
   */
  async register(userData: RegisterData): Promise<AuthResponse> {
    try {
      // Validate input
      const validatedData = RegisterSchema.parse(userData);

      console.log('📝 Attempting registration for:', validatedData.email);

      // Make registration request
      const response = await apiClient.post<AuthResponse>('/auth/signup', validatedData);

      if (response.data.success && response.data.token) {
        // Store authentication token
        apiClient.setAuthToken(response.data.token);

        console.log('✅ Registration successful for:', response.data.user.email);

        return response.data;
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('❌ Registration failed:', error);

      // Handle validation errors
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        throw new Error(firstError.message);
      }

      // Handle API errors
      if (error.code) {
        switch (error.code) {
          case 'USER_EXISTS':
            throw new Error('An account with this email already exists');
          case 'RATE_LIMIT_EXCEEDED':
            throw new Error('Too many registration attempts. Please try again later.');
          default:
            throw new Error(error.message || 'Registration failed');
        }
      }

      throw new Error(error.message || 'Registration failed');
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      console.log('🚪 Attempting logout');

      // Make logout request
      await apiClient.post<LogoutResponse>('/auth/logout');

      // Clear stored tokens
      apiClient.removeAuthToken();

      console.log('✅ Logout successful');
    } catch (error: any) {
      console.error('❌ Logout error:', error);

      // Clear tokens even if logout request fails
      apiClient.removeAuthToken();

      // Don't throw error for logout - always succeed locally
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return apiClient.isAuthenticated();
  }

  /**
   * Get current user authentication status
   */
  async getAuthStatus(): Promise<{ authenticated: boolean; user: User | null }> {
    try {
      return await apiClient.getAuthStatus();
    } catch (error) {
      console.error('Failed to get auth status:', error);
      return { authenticated: false, user: null };
    }
  }

  /**
   * Refresh authentication status
   */
  async refreshAuth(): Promise<User | null> {
    try {
      const status = await this.getAuthStatus();
      return status.user;
    } catch (error) {
      console.error('Failed to refresh auth:', error);
      return null;
    }
  }

  /**
   * Handle OAuth callback token
   */
  async handleOAuthCallback(token: string): Promise<User | null> {
    try {
      console.log('🔐 Processing OAuth callback...');

      if (!token) {
        throw new Error('No token provided in OAuth callback');
      }

      // Store the token
      apiClient.setAuthToken(token);

      // Get user information
      const authStatus = await this.getAuthStatus();

      if (authStatus.authenticated && authStatus.user) {
        console.log('✅ OAuth callback processed successfully');
        return authStatus.user;
      } else {
        throw new Error('Failed to authenticate with provided token');
      }
    } catch (error) {
      console.error('❌ OAuth callback processing failed:', error);
      apiClient.removeAuthToken();
      return null;
    }
  }

  /**
   * Initiate Google OAuth flow
   */
  initiateGoogleAuth(): void {
    const backendUrl = import.meta.env.VITE_API_URL || 'https://dashboard.bizamply.com';
    window.location.href = `${backendUrl}/auth/google`;
  }

  /**
   * Validate password strength
   */
  validatePassword(password: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    if (!/[@$!%*?&]/.test(password)) {
      errors.push('Password must contain at least one special character (@$!%*?&)');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Get password strength score (0-4)
   */
  getPasswordStrength(password: string): number {
    let score = 0;

    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[@$!%*?&]/.test(password)) score++;

    return score;
  }

  /**
   * Get password strength label
   */
  getPasswordStrengthLabel(score: number): string {
    switch (score) {
      case 0:
      case 1:
        return 'Very Weak';
      case 2:
        return 'Weak';
      case 3:
        return 'Fair';
      case 4:
        return 'Good';
      case 5:
        return 'Strong';
      default:
        return 'Unknown';
    }
  }
}

// Create and export singleton instance
const authService = new AuthService();
export default authService;
